import { WarningLevel, WarningType } from "../../WarningDescription.js";
import { standardWarningDescriptions } from "../../Configurations/WarningConfigurations.js";

export type WarningPriorityFields = {
    warningLevel: WarningLevel;
    /** Absent values sort as 0 (legacy map ordering treated missing priority as NaN). */
    warningTypePriority?: number;
};

export type WarningResolutionState = "absent" | "resolved" | "active";

/** Composite sort key: higher warning level dominates, then warningTypePriority within a level. */
export const getWarningSortScore = (warning: WarningPriorityFields): number =>
    warning.warningLevel * 10000 + (warning.warningTypePriority ?? 0);

/** Descending priority: highest-severity warning first. */
export const compareWarningsByPriority = (a: WarningPriorityFields, b: WarningPriorityFields): number =>
    getWarningSortScore(b) - getWarningSortScore(a);

/**
 * Resolution state of a warning version: whether the warning is active or not. Deliberately
 * ignores warningLevel and content fields — only resolution-state transitions are re-notify
 * events. The single predicate the notification diff reads instead of `deleted` directly,
 * so manual-resolve later becomes a one-line widening here.
 */
export const stateOf = (version: { deleted: boolean } | null | undefined): WarningResolutionState => {
    if (!version) return "absent";
    return version.deleted ? "resolved" : "active";
};

export type WarningFieldsFromType = {
    warningType: WarningType;
    warningTitle: string;
    warningDetails: string;
    warningTypePriority: number;
    warningLevel: WarningLevel;
    SMSSent: boolean;
    seen: boolean;
    warningSourceKey?: string;
};

export const createFromWarningType = (
    warningType: WarningType,
    options: Partial<Omit<WarningFieldsFromType, "SMSSent" | "seen">> = {},
): WarningFieldsFromType => {
    let warningDefaults: Partial<WarningFieldsFromType> = {};
    if (standardWarningDescriptions[warningType]) {
        const defaultInfo = standardWarningDescriptions[warningType];
        warningDefaults = {
            warningTypePriority: defaultInfo.priority,
            warningDetails: defaultInfo.details,
            warningLevel: defaultInfo.possibleWarningLevels[0],
        };
    }

    return {
        warningType: warningType,
        warningTitle: warningType,
        warningDetails: "",
        warningTypePriority: 999,
        warningLevel: WarningLevel.WARNING,
        ...warningDefaults,
        ...options,
        // new warnings are always unseen and unsent
        SMSSent: false,
        seen: false,
    };
};
